<template>
  <div class="mb-8">
    <v-row class="my-0">
      <v-col cols="12" class="py-0">
        <HeadingBox icon="mdi-file-move" title="Vejledninger" :loading='logsLoading'/>
      </v-col>
    </v-row>
    <v-row class="my-0">
      <v-col cols="12" md="8" lg="5" class="py-0 pl-3 pl-md-16 pl-lg-16 mb-4 mb-lg-0">
        <v-select v-model="selectedFiles" :items="files" :loading="getFilesLoading" :disabled="getFilesLoading" item-value="url" item-text="fileName" label="Vejledninger" multiple hide-details return-object/>
      </v-col>
      <v-col cols="12" lg="7" class="py-0 pl-3 pl-md-16 pl-lg-3 mt-lg-3">
        <div class='d-lg-flex justify-lg-end'>
          <v-radio-group hide-details row v-model="type" class="my-0 pt-0 mb-4 mb-lg-0">
            <v-radio class="my-2 mr-10" label="e-Boks" :value="2" color="primary" :disabled='patientDoesntHaveEboks'/>
            <v-radio class="my-2 mr-10" label="SMS" :value="1" color="primary" :disabled='!patientMobilePhone'/>
            <v-radio class="my-2 mr-10" label="Email" :value="0" color="primary" :disabled="!patientEmail"/>
          </v-radio-group>
          <v-btn depressed :color="buttonColor" :loading="sendLoading" :disabled="sendLoading || (type === null || !selectedFiles)" @click="send">Send</v-btn>
        </div>
      </v-col>
    </v-row>
    <v-row class="my-0">
      <v-col cols="12" lg="6" class="py-0 pl-3 pl-md-16 mt-8">
        <template v-if="logs.length > 0">
          <div v-for="logItem in logs" class="mb-2 d-flex flex-wrap align-center">
            <v-tooltip bottom :allow-overflow="true">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" size="20" class="mr-2">{{ typeIcon(logItem['communicationChannel']) }}</v-icon>
              </template>
              <span>{{ typeText(logItem['communicationChannel']) }}</span>
            </v-tooltip>
            <v-tooltip bottom :allow-overflow="true">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" size="20" :class="['mr-4', logStatusIconColor(logItem['status'])]">{{ logStatusIconName(logItem['status']) }}</v-icon>
              </template>
              <span>{{ logStatusText(logItem['status']) }}</span>
            </v-tooltip>
            <span class="text-body-1 mr-4 text-no-wrap">{{ formattedDate(logItem['sentDate']) }}</span>
            <v-tooltip v-if="logItem['content']" bottom :allow-overflow="true">
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon @click="openPreviewDialog(logItem['content'])">
                  <v-icon size="20">mdi-eye</v-icon>
                </v-btn>
              </template>
              <span>Forhåndsvisning</span>
            </v-tooltip>
            <EboksAttachment
              v-for="attachment in (logItem['eboksMessageAttachments'] || [])"
              :key="`patient-instructions-${logItem['createdDate']}-${attachment['id']}`"
              :visit-id="visitItem.id"
              :attachment-id="attachment['id']"
              :attachment-type="attachment['type']"
            />
          </div>
        </template>
        <span v-if="logs.length === 0" class="d-block text-body-1 grey--text">Ingen data...</span>
      </v-col>
    </v-row>
    <v-dialog v-model="previewDialog" width="700" overlay-color="#fff" :overlay-opacity="0.7" @click:outside="closePreviewDialog">
      <v-card>
        <v-card-text class="py-4">
          <div v-html="previewDialogContent" class="pa-3 rounded" style="white-space: pre-line;background-color: rgba(0, 0, 0, 0.05);font-size: 12px; line-height: 2; font-family: monospace, monospace;"/>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="black darken-1" text @click="previewDialog = false; closePreviewDialog()">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import methods from '../../utils/methods';
  import api from '../../api/api';
  import HeadingBox from '../common/HeadingBox';
  import EboksAttachment from '@/components/common/EboksAttachment.vue';
  import logStatusIconMixin from '@/mixins/logStatusIconMixin';

  export default {
    name: 'OverviewPatientInstructions',
    components: {HeadingBox, EboksAttachment},
    mixins: [logStatusIconMixin],

    props: {
      visitItem: {
        type: Object,
      },
      patientEmail: {
        type: String
      },
      patientMobilePhone: {
        type: String
      },
      patientDoesntHaveEboks: {
        type: Boolean
      }
    },

    data: function () {
      return {
        logsLoading: true,
        logs: [],
        previewDialog: false,
        previewDialogContent: null,
        files: [],
        selectedFiles: null,
        type: null,
        buttonColor: 'primary',
        getFilesLoading: true,
        sendLoading: false,
      };
    },

    mounted: function () {
      this.getFiles();
      this.getLogs();
      this.setType();
    },

    watch: {
      patientDoesntHaveEboks(newValue, oldValue) {
        this.setType();
      }
    },

    methods: {
      openPreviewDialog: function (value) {
        this.previewDialogContent = value;
        this.previewDialog = true;
      },
      closePreviewDialog: function () {
        this.previewDialog = false;
        setTimeout(() => {
          this.previewDialogContent = null;
        }, 100);
      },
      getLogs: async function () {
        try {
          this.logsLoading = true;
          let logsData = await api.getPatientInstructionsLogs(this.visitItem.id);
          this.logs = logsData.data || [];
        } catch (e) {} finally {
          this.logsLoading = false;
        }
      },
      getFiles: async function () {
        try {
          let filesData = await api.getPatientInstructions();
          this.files = filesData.data || [];
        } catch (e) {} finally {
          this.getFilesLoading = false;
        }
      },
      send: async function () {
        const queryParams = {
          visitId: this.visitItem.id,
          files: this.selectedFiles,
          messageType: this.type,
        };
        try {
          this.buttonColor = 'primary';
          this.sendLoading = true;
          await api.setPatientInstructions(queryParams);
        } catch (e) {
          this.buttonColor = 'error';
        } finally {
          this.getLogs();
          this.sendLoading = false;
        }
      },
      setType: function () {
        if (this.patientDoesntHaveEboks && this.patientMobilePhone) {
          this.type = 1;
          return;
        }
        if (this.patientDoesntHaveEboks && this.patientEmail) {
          this.type = 0;
          return;
        }
        if (!this.patientDoesntHaveEboks) {
          this.type = 2;
          return;
        }
        this.type = null;
      },
      formattedDate: function (value) {
        if (value) {
          return`${methods.getDateFromUtc(value)}, ${methods.getTimeFromUtc(value)}`;
        }
        return 'Afsendelse planlagt..';
      },
      typeIcon: function (type) {
        let names = {
          0: 'mdi-at',
          1: 'mdi-message-text',
          2: 'mdi-alpha-e-box'
        };
        return names[type];
      },
      typeText: function (type) {
        let text = {
          0: 'Email',
          1: 'SMS',
          2: 'e-Boks'
        };
        return text[type];
      },
    },
  };
</script>
